/**
 * Editor buffers that reach the workspace store on a debounce, and a way to
 * push all of them in NOW.
 *
 * WHY: a query tab copies its Monaco buffer into the workspace store 200 ms
 * after the last keystroke (`useQueryBufferSync`). A session save reads the
 * store, so an immediate save — the one the app runs when a window closes or
 * the app quits (`saveSessionImmediately`) — missed whatever was typed inside
 * that window, and an immediate quit lost it. Each host that debounces buffer
 * text into the store registers its synchronous trailing flush here;
 * `saveSessionImmediately()` calls `flushPendingBuffers()` first.
 *
 * Hosts today: query tabs (`useQueryBufferSync`), and the storage preview's
 * unsaved file drafts, whose persisted write is debounced
 * (`storagePreviewSessionStore.ts`). Notebook cells, the PL/SQL
 * debugger, the Table Designer's SQL view and the other editors write every
 * change straight into the store, so they have nothing pending. A new editor
 * host that debounces into the store must register here too.
 */

type PendingBufferFlush = () => void;

const flushes = new Set<PendingBufferFlush>();

/** Register a synchronous flush of a pending debounced buffer; returns the unregister. */
export function registerPendingBufferFlush(flush: PendingBufferFlush): () => void {
  flushes.add(flush);
  return () => {
    flushes.delete(flush);
  };
}

/** Push every pending editor buffer into the store now. One failing host does not stop the rest. */
export function flushPendingBuffers(): void {
  for (const flush of Array.from(flushes)) {
    try {
      flush();
    } catch (error) {
      console.error('[PendingBufferFlushes] Flushing an editor buffer failed:', error);
    }
  }
}

/** Test-only: drop every registration. */
export function _resetPendingBufferFlushesForTests(): void {
  flushes.clear();
}
